<template>
  <v-dialog
    data-testid="batch-order-editor-dialog"
    :model-value="modelValue"
    fullscreen
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="bm-overlay" data-testid="order-editor" role="dialog" :aria-label="`Edit order ${orderLabel}`">
      <header class="bm-overlay__head">
        <div class="bm-overlay__headings">
          <h2 class="bm-overlay__title">Edit {{ firstName }}'s order</h2>
          <!--
            WHO THIS ORDER BELONGS TO. Jake, 2026-09-22: "We need the
            shopper's full name and email address in the order editor, not
            just the batch card."

            IT LIVES IN THE HEADER, AND THE PLACEMENT IS THE ARGUMENT.
            The header already establishes identity -- the title greets by
            first name and the line below states which order, when it was
            placed, and which batch holds it. Full name and email are the
            same KIND of fact: who this order belongs to. Putting them here
            keeps ONE identity region instead of opening a second one
            somewhere down the overlay, and it keeps them out of a product
            card, where they would read as a property of the garment rather
            than of the order.

            It sits ABOVE the order metadata, not below, because the title
            it qualifies is directly above it: "Edit Ada's order" followed
            immediately by which Ada. Two shoppers with the same first name
            in one batch are indistinguishable from the title alone, which
            is the gap this closes -- the greeting is a salutation, not an
            identification.

            The email is a real mailto, same as the batch card, because the
            pre-release window exists for FIXING orders and fixing one often
            means asking the person who placed it. Rendered only when there
            is one: `checkout_fields` is nullable and unconstrained, the
            backend helper returns "" for a missing "Company Email", and a
            guest order with no address is a real state. An empty `mailto:`
            is worse than showing nothing.

            No new fetch and no serializer change: `shopper_email` is
            already served by `_orders_for_batches` and already mapped to
            `shopperEmail` by `normalizeOrder`, which is the same object
            this overlay receives.
          -->
          <p class="bm-overlay__who">
            <span class="bm-overlay__shopper" data-testid="edit-shopper-name">{{ order.shopper }}</span>
            <template v-if="order.shopperEmail">
              <span class="bm-overlay__whodot" aria-hidden="true">&middot;</span>
              <a
                class="bm-overlay__email"
                :href="`mailto:${order.shopperEmail}`"
                data-testid="edit-shopper-email"
                >{{ order.shopperEmail }}</a
              >
            </template>
          </p>
          <p class="bm-overlay__sub" data-testid="order-editor-sub">
            {{ orderLabel }} &middot; placed {{ order.placed }} &middot; in batch {{ batch.id }}, which has not been released
          </p>
          <!--
            WHERE THE ORDER IS GOING. Jake, 2026-09-22: "The shopper's
            shipping address should be included in the order editor also."

            SAME IDENTITY REGION, OWN LINE, BELOW THE METADATA. It is the
            same kind of fact as the name and email -- whose order this is
            and where it goes -- so it extends the identity block rather
            than opening a third region. But it is a DESTINATION rather
            than a CONTACT, which is why it sits under the order metadata
            instead of joining the name/email line: those two are how you
            reach the person, this is where the goods land, and running all
            three together would make a single line nobody can scan.

            RENDERED INLINE, NOT AS A STACKED POSTAL BLOCK. A four-line
            address is the conventional shape, but this header is chrome
            above a scrolling body -- four more lines pushes the charge
            note and the first product card toward the fold, on the one
            surface whose job is editing those cards. Nobody posts a letter
            from this screen; they check the destination is right and read
            it out to someone. One scannable line serves that and costs
            three fewer.

            The comma-joining happens in `shipToLine`, which omits absent
            parts rather than emitting empty slots -- every column is
            nullable and an address-less order is the COMMON case (7 of 276
            orders on the 8083 sandbox carry one). The whole line is hidden
            when there is nothing to show, so there is no dangling label.

            The address is the order's FROZEN snapshot, not the client's
            current address. That is deliberate at the source
            (`models/order.py:440-470`) and asserted on the wire: showing a
            live address would name a destination this order is not going
            to.
          -->
          <p
            v-if="shipToLine"
            class="bm-overlay__shipto"
            data-testid="edit-ship-to"
          >
            <i class="material-icons bm-overlay__shiptoicon" aria-hidden="true">local_shipping</i>
            <span>Ships to {{ shipToLine }}</span>
          </p>
        </div>
        <button type="button" class="bm-iconbtn" data-testid="edit-close" aria-label="Close" @click="close">
          <i class="material-icons" aria-hidden="true">close</i>
        </button>
      </header>

      <div class="bm-overlay__chargenote">
        <!-- When money moves, third of three levels (#3184). -->
        <ChargeAtReleaseNote
          testid="edit-charge-at-release"
          extra="Editing here is safe -- this order has not been paid for yet."
        />
      </div>

      <div class="bm-overlay__body">
        <!--
          ONE CARD PER PRODUCT, a quantity stepper PER SIZE.

          The shopper's lines are grouped by product (Jake: "do we get
          quantity buttons for each size?" -- yes). Two lines of the same
          garment in different sizes are two `order_items` rows on the wire
          but one card here, because that is how a person thinks about it.
        -->
        <div
          v-for="(group, i) in groups"
          :key="group.key"
          class="bm-line"
          :data-testid="`line-${i}`"
        >
          <div class="bm-line__top">
            <!--
              The garment, identified. Everything here comes off the ORDER's
              own line; there is no catalog to fall back to, deliberately.
            -->
            <img
              v-if="group.imageUrl"
              class="bm-line__img"
              :src="group.imageUrl"
              :alt="group.productName || 'Product image'"
              :data-testid="`product-image-${i}`"
            />
            <div
              v-else
              class="bm-line__img bm-line__img--none"
              :data-testid="`product-image-missing-${i}`"
              aria-hidden="true"
            >
              <i class="material-icons">image_not_supported</i>
            </div>

            <div class="bm-line__ident">
              <div class="bm-line__name" :data-testid="`product-name-${i}`">
                {{ group.productName || "Unnamed product" }}
              </div>
              <div class="bm-line__meta">
                <span v-if="group.sku" :data-testid="`product-sku-${i}`">{{ group.sku }}</span>
                <span v-if="group.sku && group.decoration" aria-hidden="true">&middot;</span>
                <span v-if="group.decoration">{{ group.decoration }}</span>
              </div>
            </div>

            <div class="bm-line__amount" :data-testid="`line-total-${i}`">
              {{ money(groupTotal(group)) }}
            </div>
          </div>

          <div class="bm-line__controls">
            <div v-if="group.availableColors.length" class="bm-line__colors">
              <div class="bm-field__label">
                Color &middot; <span class="bm-field__value">{{ group.color }}</span>
              </div>
              <!--
                The container carries a testid so a ui scenario can assert
                the swatches stay INSIDE it. Without one the only anchors are
                the individual swatches, whose ids are the seeded colour
                NAMES and therefore differ per fixture.
              -->
              <div class="bm-swatches" :data-testid="`swatches-${i}`">
                <!--
                  The product's OWN colours, with the product's OWN hex. A
                  colour the ten-entry `COLOR_HEX` map never heard of now
                  renders in its real colour instead of grey.
                -->
                <button
                  v-for="c in group.availableColors"
                  :key="c.id"
                  type="button"
                  class="bm-swatch"
                  :class="{ 'bm-swatch--on': c.id === group.color, 'bm-swatch--nohex': !c.hex }"
                  :style="c.hex ? { background: c.hex } : null"
                  :title="c.name"
                  :aria-label="c.name"
                  :aria-pressed="String(c.id === group.color)"
                  :data-testid="`color-${i}-${String(c.id).replace(/\s/g, '')}`"
                  @click="setColor(group, c.id)"
                >
                  <span v-if="!c.hex" class="bm-swatch__label">{{ shortColor(c.name) }}</span>
                </button>
              </div>
            </div>

            <!--
              The logo, CHANGEABLE and filtered to this colour.

              Rendered only when the colour actually carries logos: an
              empty picker reads as a loading failure, and an undecorated
              product legitimately has none. When the product IS decorated
              but this colour carries nothing, the sibling `v-else-if`
              below explains the gap instead -- silence there was read as
              a rendering bug on order 270. The options come from the
              colour-keyed map the API serves, which is built by the SAME
              resolver that backs the shopper's PDP picker and the backend
              write guard -- so what is offered here is exactly what the
              shopper was offered and exactly what will be accepted.

              This filter is for usability, NOT safety: the refusal lives
              in `batch_order_edit._assert_logo_eligible`. SAFRS exposes
              the column directly, so a hidden option is not a guard.
            -->
            <div v-if="group.logoOptions.length" class="bm-line__logos" :data-testid="`logo-field-${i}`">
              <div class="bm-field__label">Logo</div>
              <div class="bm-logos">
                <!--
                  The testid is POSITIONAL, and the artwork id rides along
                  in `data-logo-id`.

                  The natural testid would be `logo-<card>-<artwork_id>`,
                  but a ui-runner scenario cannot use it: selectors are
                  exact `data-testid=` matches with no globbing, and a
                  seeded artwork id shifts with every reseed, so "click the
                  OTHER logo" would be inexpressible. `order-edit-<n>` on
                  BatchOrderRow exists for exactly this reason.

                  An element gets ONE data-testid, so the position wins and
                  the id becomes a readable attribute -- which the
                  component specs assert against directly, losing nothing.
                -->
                <button
                  v-for="(logo, li) in group.logoOptions"
                  :key="logo.id"
                  type="button"
                  class="bm-logo"
                  :class="{ 'bm-logo--on': logo.id === group.logoId }"
                  :aria-pressed="String(logo.id === group.logoId)"
                  :data-testid="`logo-option-${i}-${li}`"
                  :data-logo-id="logo.id"
                  @click="setLogo(group, logo.id)"
                >
                  <DecorationArtThumb
                    v-if="logo.image_url"
                    class="bm-logo__img"
                    :src="logo.image_url"
                    :ink-hex="logo.dominant_ink_hex"
                    :ink-mixed="logo.dominant_ink_mixed === true"
                    :alt="logo.label || 'Logo'"
                  />
                  <span v-else class="bm-logo__img bm-logo__img--none" aria-hidden="true">
                    <i class="material-icons">image_not_supported</i>
                  </span>
                  <span class="bm-logo__text">
                    <span class="bm-logo__name">{{ logo.label || logo.file_number }}</span>
                    <!--
                      The back office's own id for this art. Shown because
                      this is an INTERNAL view -- it is how someone checks
                      a line against what the decorator will receive. A
                      shopper must never see it.
                    -->
                    <span v-if="logo.design_code" class="bm-logo__code">{{ logo.design_code }}</span>
                  </span>
                </button>
              </div>
              <!--
                Say why the list is short, rather than leaving a distributor
                to wonder where a logo went. The exclusion is real product
                data, not an error.
              -->
              <p class="bm-line__scopenote" :data-testid="`logo-scope-note-${i}`">
                Only logos available for {{ group.color }} are shown.
              </p>
            </div>

            <!--
              THE IMPASSE NOTE, where the picker would have been.

              Quiet and informational, NOT an error state -- the order is
              still editable and still saveable, which is the whole point
              of the backend change that accompanies this. An alert here
              would tell a distributor to stop when the correct advice is
              that they can carry on.

              POINTS AT THE PRODUCT, NOT AT THE COLOUR CHOICE. Jake traced
              order 270's L500 to a half-configured product: its only LIVE
              placement has ZERO variants, so the resolver returns nothing
              for EVERY colour, not just this one. The distributor did not
              pick a bad colour -- someone deleted a placement and added a
              replacement that was never finished. Copy blaming the colour
              would send them to change the one control that cannot help.
              The colour is still NAMED, because it is what they are
              looking at, but the sentence attributes the gap to the
              product's setup.
            -->
            <div
              v-else-if="group.logoImpasse"
              class="bm-line__logos"
              :data-testid="`logo-impasse-${i}`"
            >
              <div class="bm-field__label">Logo</div>
              <p class="bm-line__scopenote bm-line__scopenote--impasse">
                This product has no decoration set up for {{ group.color }},
                so there is no logo to choose. The order can still be saved.
              </p>
            </div>

            <div v-if="group.sizeRows.length" class="bm-line__sizes">
              <div class="bm-field__label">Quantity per size</div>
              <!--
                A HORIZONTAL band of size cells, borrowed from the shopper
                PDP's desktop matrix (`sf-product__matrix-grid`). Jake,
                2026-09-22: "take a tip from the product detail page on the
                storefront to show sizes in a horizontal list instead of a
                vertical one, with the +/- buttons above and below each
                quantity -- that will save a bunch of whitespace."

                So the SIZES run across and each cell stacks + / qty / -
                vertically. On a card with eight sizes the old one-row-per-
                size column was eight 44px rows tall; this is one.

                THIS COMMENT WAS TRUE ABOUT THE INTENT AND FALSE ABOUT THE
                CODE from #3705 until 2026-09-22, and it is worth knowing
                why. The markup was changed to cells and the class named
                `bm-sizegrid`, but the stylesheet kept `flex-direction:
                column` and never grew a `.bm-sizecell` rule at all -- so
                every cell was an unstyled full-width block and the band was
                a tall column. Jake compared it to the PDP and rejected it.
                Read the CSS, not this paragraph, if the two ever disagree
                again.

                EVERY size is steppable now, including one nobody ordered
                and one the product no longer offers. Jake, same day: "this
                is a distributor making the decision. They should be allowed
                to make a size exception even if the store product doesn't
                have a specific size available." The backend agrees -- the
                add path deliberately does not consult the product's current
                size list.
              -->
              <div class="bm-sizegrid" :data-testid="`size-grid-${i}`">
                <div
                  v-for="row in group.sizeRows"
                  :key="row.size"
                  class="bm-sizecell"
                  :class="{ 'bm-sizecell--empty': row.qty < 1 }"
                  :data-testid="`size-row-${i}-${row.size}`"
                >
                  <span class="bm-sizecell__label">{{ row.size }}</span>

                  <!--
                    Per-size testids on BOTH buttons, not just the field.
                    ui-runner selectors are exact `data-testid=` matches with
                    no globbing, so the stepper's default ids would be eight
                    identical nodes on this card and a scenario could not say
                    "add one XL".

                    `min-value` 0 because 0 here MEANS something: take this
                    size off the order. The cart floors at 1 for the opposite
                    reason -- a 0 there would silently delete the line the
                    shopper is editing.
                  -->
                  <quantity-stepper
                    vertical
                    compact
                    :model-value="row.qty"
                    :size-label="row.size"
                    :qty-testid="`qty-${i}-${row.size}`"
                    :increment-testid="`qty-${i}-${row.size}-plus`"
                    :decrement-testid="`qty-${i}-${row.size}-minus`"
                    :min-value="0"
                    @update:model-value="setSizeQty(group, row, $event)"
                  />
                </div>
              </div>

              <!--
                The note that used to apologise for the missing affordance is
                GONE, because the affordance exists. What replaces it states
                the one thing that is still true: emptying an order entirely
                is a cancellation, which is a different act with its own
                button and its own confirmation.
              -->
              <p class="bm-line__scopenote" :data-testid="`size-help-note-${i}`">
                Set any size to 0 to take it off the order. To drop the whole order,
                cancel it below.
              </p>
            </div>
          </div>
        </div>

        <!-- Price is not editable, and the UI says why. -->
        <div class="bm-pricelock" data-testid="price-locked-note">
          <i class="material-icons bm-pricelock__icon" aria-hidden="true">lock</i>
          <div>
            <b>Prices can't be edited.</b> Editing is here to fix mistakes, not to change what the store
            charges. Sizes and quantities do move the total &mdash; a 2XL can cost more than a large.
          </div>
        </div>
      </div>

      <footer class="bm-overlay__foot bm-editfoot" data-testid="edit-footer">
        <div class="bm-editfoot__totals">
          <!--
            The arithmetic is shown, not just its answer. This footer used to
            print the line-item sum alone while the order was stored (and will
            be charged) net of the free-item discount, so it read $432 on an
            order worth $297. A total that is simply smaller is no better: the
            complaint was that it was not obvious WHY. Sub-total and discount
            are rendered whenever a discount applies, so the total can be
            checked on screen. Same reason as CartSummary.vue's row -- an
            unexplained discount at the till reads as a pricing bug.
          -->
          <div v-if="discount > 0" class="bm-editfoot__lines">
            <div class="bm-editfoot__line" data-testid="edit-subtotal">
              <span>Items</span><span>{{ money(subTotal) }}</span>
            </div>
            <div class="bm-editfoot__line" data-testid="edit-free-item-discount">
              <span>Free item</span><span>-{{ money(discount) }}</span>
            </div>
          </div>
          <div class="bm-editfoot__amounts">
            <span v-if="changed" class="bm-editfoot__was" data-testid="edit-old-total">{{ money(was) }}</span>
            <span class="bm-editfoot__now" data-testid="edit-new-total">{{ money(now) }}</span>
            <span
              v-if="changed"
              class="bm-editfoot__delta"
              :class="delta > 0 ? 'bm-editfoot__delta--up' : 'bm-editfoot__delta--down'"
              data-testid="edit-delta"
            >{{ delta > 0 ? '+' : '-' }}{{ money(Math.abs(delta)) }}</span>
          </div>
          <div class="bm-editfoot__sentence" data-testid="edit-charge-sentence">
            This is {{ firstName }}'s order total for batch {{ batch.id }}.
          </div>
        </div>
        <!--
          Cancel sits WITH the other two decisions (Jake #4). It was at the
          bottom of the scrolling body, so on a long order the destructive
          action was the one you met first and Save was off-screen.
        -->
        <div class="bm-editfoot__actions">
          <button
            v-if="order.canCancel !== false"
            type="button"
            class="bm-btn bm-btn--dangerquiet"
            data-testid="cancel-order-open"
            @click="confirmCancel = true"
          >
            Cancel order
          </button>
          <!--
            "Discard changes", not "Discard". Next to "Cancel order" a bare
            "Discard" is ambiguous about its object -- the changes or the
            order -- and the two buttons sit side by side with very
            different consequences. The testid is unchanged on purpose: it
            is a contract the ui-runner scenarios bind to, and renaming it
            for a copy edit would take them red.
          -->
          <button type="button" class="bm-btn" data-testid="edit-discard" @click="close">Discard changes</button>
          <button
            type="button"
            class="bm-btn bm-btn--primary"
            data-testid="edit-save"
            :disabled="!changed"
            @click="save"
          >
            Save changes
          </button>
        </div>
      </footer>

      <!-- House destructive dialog: red header, plain statement of consequence. -->
      <div v-if="confirmCancel" class="bm-scrim" data-testid="cancel-order-dialog">
        <div class="bm-confirm">
          <div class="bm-confirm__head">Cancel {{ order.shopper }}'s order?</div>
          <div class="bm-confirm__body">
            {{ orderLabel }} leaves batch {{ batch.id }} and won't be made. Nothing has been charged, so there
            is no refund to issue. This can't be undone &mdash; the shopper would need to order again.
          </div>
          <div class="bm-confirm__foot">
            <button type="button" class="bm-btn" data-testid="cancel-order-back" @click="confirmCancel = false">
              Keep the order
            </button>
            <button
              type="button"
              class="bm-btn bm-btn--danger"
              data-testid="cancel-order-confirm"
              @click="$emit('cancel-order', order)"
            >
              Cancel this order
            </button>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
/**
 * The order editor: colour and per-size quantity, against the REAL product.
 *
 * WHAT THIS REPLACED
 * ==================
 *
 * This component shipped as a design prototype wired to fake data. It
 * imported `STYLES`, `SIZES`, `SIZE_UPCHARGE` and `COLOR_HEX` from
 * `batchManagementService`, and all four were invented: four made-up
 * garments, a fixed ten-colour hex map, invented upcharges, and `SIZES` as a
 * module-level CONSTANT rendered for every line whatever the product was.
 * Jake, on the live AdventHealth store: no images, no product names, no SKUs,
 * and "it's listing sizes for items which are OSFA" -- an OSFA cap got eight
 * size chips because a constant cannot know what the product is.
 *
 * Every option now comes off the order's own line (`availableColors`,
 * `availableSizes`, `productName`, `sku`, `imageUrl`), served per line by the
 * API from the campaign product. Absent data renders as absent; there is no
 * catalog to fall back to, which is the point.
 *
 * WHY THERE IS NO SIZE-UPCHARGE CHIP
 * ==================================
 *
 * The design bundle locks "sizes show their upcharge on the chip (2XL +$3)".
 * That is not implementable from the client: the real per-size cost lives in
 * `added_cost_and_margin` behind a four-level margin hierarchy, and the
 * authoritative price is recomputed SERVER-side on save (`_refreeze_line_price`
 * -> `compute_order_item_price_snapshot`, the same function that writes
 * `order_items.unit_price` at checkout). The old chip printed +$3 from a
 * constant that had no relationship to any real product. A missing affordance
 * beats a confidently wrong number on a real order, so the base price served
 * per line is what is displayed, and the footer's delta is what moves.
 *
 * ADD AND REMOVE ARE AVAILABLE (changed 2026-09-22)
 * =================================================
 *
 * This comment used to say they were not, and that was true: there was no
 * add-line or delete-line endpoint and `quantity: 0` was refused, so an
 * unordered size rendered the words "not ordered" instead of a control.
 *
 * Jake reopened it: "All the sizes should have +/- buttons, not just the ones
 * from the original order." `add_order_item_size` and `remove_order_item_size`
 * now back that, reached through the same PATCH this editor already used.
 *
 * So EVERY size the product offers is steppable, an unordered one sits at 0,
 * and stepping an ordered one to 0 removes its line. A size the product no
 * longer offers but which is on the order stays steppable too -- Jake, same
 * day: "this is a distributor making the decision. They should be allowed to
 * make a size exception even if the store product doesn't have a specific
 * size available."
 *
 * THE SHOPPER IS IDENTIFIED IN THE HEADER (added 2026-09-22)
 * ========================================================
 *
 * Full name and email sit under the title, not in a product card. Jake asked
 * for them and left the placement open; the argument for the header is that
 * it is where identity already lives -- the title greets by first name and
 * the line below names the order, when it was placed, and its batch. Name
 * and email answer the same question those do ("whose order is this?"), so
 * they extend the one identity region rather than starting a second one.
 *
 * Both values arrive on the order this overlay is already given --
 * `_orders_for_batches` serves `shopper_email` and `normalizeOrder` maps it
 * to `shopperEmail`. No extra fetch, no serializer change. Note the OTHER
 * batch route, `GET /batches/<id>/`, does NOT serve the email; it is not the
 * payload behind this surface, and adding the field there for symmetry alone
 * would ship a field with no reader.
 *
 * Style editing is still gone: `changeStyle` used to overwrite a real order's
 * product from the fake catalog. Adding a whole new PRODUCT is likewise not
 * here -- an added size inherits colour and logo from a sibling line, and a
 * product with no sibling has no answer to "which colour".
 *
 * The totals are NET of the frozen free-item discount, and the footer shows
 * the sub-total and the discount alongside so the arithmetic reconciles on
 * screen. The discount is displayed, never recomputed -- the backend freezes
 * it per line at checkout and charges that value.
 */
import ChargeAtReleaseNote from "./ChargeAtReleaseNote.vue";
import DecorationArtThumb from "@/components/decoration/DecorationArtThumb.vue";
// The SHARED stepper, not a third hand-rolled one. This file used to carry
// its own `bm-stepper` markup, which meant two definitions of press-and-hold
// and two chances to reintroduce the touch double-fire and runaway-timer bugs
// `QuantityStepper` exists to prevent. Its `vertical` arrangement was added
// for this surface rather than forked into it.
import QuantityStepper from "@/components/Storefront/QuantityStepper.vue";
import {
  money,
  lineTotal,
  orderDiscount,
  orderSubTotal,
  orderTotal,
} from "@/services/batchManagementService";

/** Keys the PATCH endpoint accepts. Anything else 400s the whole order. */
const SAVABLE_KEYS = ["id", "styleId", "color", "size", "qty", "logoId"];

export default {
  name: "OrderEditor",
  components: { ChargeAtReleaseNote, DecorationArtThumb, QuantityStepper },
  props: {
    modelValue: { type: Boolean, default: false },
    order: { type: Object, required: true },
    batch: { type: Object, required: true },
  },
  emits: ["update:modelValue", "save", "cancel-order"],
  data() {
    return {
      // Edit a COPY. Discarding must leave the order untouched, and the
      // struck-through "was" total has to survive every edit.
      lines: (this.order.lines || []).map((l) => ({ ...l })),
      confirmCancel: false,
    };
  },
  computed: {
    firstName() {
      return String(this.order.shopper || "").split(" ")[0];
    },
    // The order number a human reads; `order.id` is the "O-<pk>" identity
    // key (see `normalizeOrder`).
    orderLabel() {
      return this.order.orderLabel || this.order.id;
    },
    /**
     * The frozen ship-to as ONE scannable line, or "" when there is none.
     *
     * BUILT BY FILTERING, NOT BY TEMPLATING. The obvious form --
     * `${city}, ${state} ${zip}` -- is what produces "Oak Park, , 91377"
     * and ", CA" on the partial addresses these nullable columns actually
     * hold. Every part is dropped before it is joined, so a missing piece
     * costs its separator too.
     *
     * The city/state/postal group is assembled first and joined to the
     * street parts with a comma, while state and postal are joined by a
     * SPACE -- "Oak Park, CA 91377" is how the line is read aloud, and a
     * comma between the last two would be wrong even when both are present.
     *
     * The recipient name is deliberately NOT included. It is the same
     * person already named two lines above, and repeating it here would
     * make the header state one fact twice. It differs only when someone
     * ships to a third party, which this surface cannot currently produce
     * -- when it can, that is the moment to show it, not before.
     *
     * Country is omitted for the same reason it is guarded server-side:
     * MVP is US-only and every address would end in a redundant "US".
     */
    shipToLine() {
      const to = this.order.shipTo;
      if (!to) return "";

      const clean = (v) => String(v == null ? "" : v).trim();
      const region = [clean(to.state), clean(to.postal_code)]
        .filter(Boolean)
        .join(" ");

      return [
        clean(to.address1),
        clean(to.address2),
        clean(to.city),
        region,
      ]
        .filter(Boolean)
        .join(", ");
    },
    /**
     * The shopper's lines, grouped into one card per PRODUCT AND COLOUR.
     *
     * Two sizes of one garment read as one thing -- that is what `sizeRows`
     * is for, covering every size the PRODUCT offers and marking which were
     * actually ordered, so an unordered size is legible as "not ordered"
     * rather than absent.
     *
     * COLOUR IS IN THE KEY, AND IT HAS TO BE (GH #3744)
     * =================================================
     *
     * This used to group on `styleId` alone. Two colours of one garment share
     * a campaign product, so they landed in ONE card -- and the card's own
     * controls assume the opposite. `setColor` and `setLogo` below apply to
     * every index in the group, precisely because colour and logo belong to
     * the garment rather than to one size; `setColor`'s docstring says lines
     * must not "end up different colours from one swatch click". That is only
     * true if a card IS one product in one colour. The key was the single
     * thing that did not encode it.
     *
     * The merge is lossless at DIFFERENT sizes -- one card, two size rows,
     * wrong but nothing missing. At a SHARED size it deletes a row: the
     * `orderedBySize` Map below is keyed on size, `Map.set` overwrites, and
     * the losing line gets no size row, no stepper, no representation at all.
     * It stays reachable by the surviving card's controls, so zeroing the
     * visible row emits the INVISIBLE line's id with quantity 0, which the
     * endpoint routes to `remove_order_item_size` -- a real delete of a line
     * the operator never saw. Reported by Jake on the production AdventHealth
     * store: three lines, two cards.
     *
     * A two-colour product therefore renders as TWO CARDS, which is a visible
     * change and the intended one. It is what the batch LIST has always
     * counted ("3 styles" for Jake's order, correctly), and it is the shape
     * every write path here already assumed.
     *
     * WHY A NULL COLOUR IS ITS OWN GROUP RATHER THAN FOLDED INTO ""
     * =============================================================
     *
     * `order_items.primary_attribute_value` is a nullable column, so a line
     * with no colour is a real record and a line with an empty-string colour
     * is a DIFFERENT real record. Collapsing them (`l.color ?? ""`) is a
     * fallback that merges the long-tail row into the common case, which
     * design-philosophy principle 5 exists to prevent -- and here the merged
     * pair inherits exactly the delete-the-invisible-line bug this change
     * removes. The sentinel below cannot collide with any colour string, so
     * the two states stay distinct. Over-splitting costs one extra card;
     * over-merging costs a deleted order line.
     */
    groups() {
      const out = [];
      const byKey = new Map();

      // Distinguishes "this line has no colour" from a colour that happens
      // to be the empty string.
      //
      // A SEPARATE FLAG IN THE KEY, not a magic colour string. Any sentinel
      // spelled as a colour can in principle collide with a real one, and a
      // control character -- the obvious way to guarantee it cannot -- makes
      // the whole file read as BINARY to grep, diff, and review tooling.
      // Measured: `file` reported "data" rather than text, and every grep
      // for a symbol in this file silently returned nothing.
      //
      // Keying on the NULLNESS itself sidesteps the question. `n` and `c`
      // cannot collide because the colour is appended after the flag, so a
      // line whose colour is literally "c" keys as `...|c|c` and a null one
      // as `...|n|`.

      this.lines.forEach((l, index) => {
        // Fall back to the index when a line has no product id, so a legacy
        // row still gets its own card instead of merging with every other.
        const colour =
          l.color == null ? "n|" : `c|${String(l.color)}`;
        const key = l.styleId != null ? `s${l.styleId}|${colour}` : `i${index}`;
        let group = byKey.get(key);
        if (!group) {
          group = {
            key,
            styleId: l.styleId,
            productName: l.productName || null,
            sku: l.sku || null,
            imageUrl: l.imageUrl || null,
            decoration: l.decoration || "",
            color: l.color,
            availableColors: l.availableColors || [],
            availableSizes: l.availableSizes || [],
            // A logo belongs to the PRODUCT, not to one size, so it is read
            // off the group's first line and applied to all of them.
            logoId: l.logoId != null ? l.logoId : null,
            availableLogos: l.availableLogos || {},
            indices: [],
          };
          byKey.set(key, group);
          out.push(group);
        }
        group.indices.push(index);
      });

      out.forEach((group) => {
        const orderedBySize = new Map();
        group.indices.forEach((index) => {
          orderedBySize.set(this.lines[index].size, index);
        });

        // Every size the product offers, in the product's own order, plus
        // any ordered size the product no longer lists (a product whose
        // selection changed after the order was placed must not make its
        // own line vanish from the editor).
        const offered = group.availableSizes.map((s) => s.id != null ? s.id : s.label);
        const extras = [...orderedBySize.keys()].filter(
          (s) => s != null && !offered.includes(s)
        );

        group.sizeRows = [...offered, ...extras].map((size) => {
          const index = orderedBySize.get(size);
          const ordered = index != null;
          return {
            size,
            ordered,
            index: ordered ? index : null,
            qty: ordered ? Number(this.lines[index].qty || 0) : 0,
          };
        });

        // The logos THIS COLOUR may carry. Read from the colour-keyed map
        // the API serves, so the option list moves with the swatch: the
        // eligible set is per (product, colour) and a logo legitimate on
        // White can be unavailable on Navy. An unknown colour yields an
        // empty list rather than the whole library -- falling back to
        // "show everything" is exactly the unfiltered picker this feature
        // exists to avoid.
        group.logoOptions = group.availableLogos[group.color] || [];

        // THE IMPASSE: this product HAS decoration, but not for the colour
        // the line is currently on.
        //
        // Jake, 2026-09-22, on order 270: "If a decoration in an order at
        // a given color has no valid decoration, then we're at an impasse.
        // This isn't a data fix. Editing an order should not be blocked
        // for this reason."
        //
        // Two different situations produce an empty `logoOptions`, and
        // they need opposite treatment:
        //
        //   - an UNDECORATED product carries no logos for ANY colour.
        //     Nothing is wrong and nothing is actionable, so the card
        //     stays silent. A note here would fire on every blank-goods
        //     line in the store and train people to ignore it.
        //   - a DECORATED product whose CURRENT colour carries none is
        //     the impasse. The distributor CAN act -- change the colour,
        //     or pick a different product -- so the card has to say so.
        //     Order 270's L500 rendered no Logo field at all and read as
        //     a rendering bug.
        //
        // Distinguished by reading the WHOLE colour-keyed map rather than
        // just this colour's entry, which is the only thing that tells
        // the two apart. `some` rather than a key count: the API can
        // serve a colour key with an empty list, and a product whose
        // every colour is empty is undecorated, not stranded.
        group.logoImpasse =
          group.logoOptions.length === 0 &&
          Object.values(group.availableLogos || {}).some(
            (entries) => (entries || []).length > 0
          );

        // Read live off the lines so the selection reflects an edit that
        // has already been applied, not the value the group was built with.
        const first = this.lines[group.indices[0]];
        group.logoId = first && first.logoId != null ? first.logoId : null;
      });

      return out;
    },
    was() {
      return orderTotal(this.order);
    },
    subTotal() {
      return orderSubTotal({ lines: this.lines });
    },
    // FROZEN. The discount is whatever checkout wrote to the line, carried
    // across edits untouched -- see the note in batchManagementService. It is
    // read off the EDITED lines rather than the original order so a line the
    // distributor removes takes its discount with it.
    discount() {
      return orderDiscount({ lines: this.lines });
    },
    // Net, so it equals what the batch will charge. `was` is netted the same
    // way, otherwise the delta between them would be off by the discount.
    now() {
      return orderTotal({ lines: this.lines });
    },
    delta() {
      return this.now - this.was;
    },
    changed() {
      return JSON.stringify(this.lines) !== JSON.stringify(this.order.lines || []);
    },
  },
  methods: {
    money,
    lineTotal,
    /** What this product's lines come to, so the card can foot itself. */
    groupTotal(group) {
      return group.indices.reduce(
        (sum, index) => sum + lineTotal(this.lines[index]),
        0
      );
    },
    /** A legible stand-in when a colour has no hex to render. */
    shortColor(name) {
      return String(name || "")
        .split(/\s+/)
        .map((w) => w.charAt(0))
        .join("")
        .slice(0, 3)
        .toUpperCase();
    },
    patch(index, changes) {
      this.lines = this.lines.map((l, j) =>
        j === index ? { ...l, ...changes } : l
      );
    },
    /**
     * Recolour the whole product.
     *
     * Colour is a property of the garment, not of one size, so every line in
     * the card moves together -- a shopper's L and XL of the same tee cannot
     * end up different colours from one swatch click.
     */
    setColor(group, color) {
      const wanted = new Set(group.indices);

      // The eligible logo set is per (product, COLOUR), so recolouring can
      // invalidate the current pick -- UChicago Medicine is offered on
      // White and Black but never on Navy. Drop a selection the new colour
      // cannot carry rather than letting it ride: the backend validates
      // against the POST-edit colour and would 403 the save, which reaches
      // the operator as a failure with no visible cause.
      //
      // Cleared to null, NOT auto-substituted with another eligible logo.
      // Silently stitching a different logo than the one on screen is the
      // precise failure this whole feature exists to prevent.
      const eligible = group.availableLogos[color] || [];
      const stillValid = (id) =>
        id != null && eligible.some((o) => o.id === id);

      this.lines = this.lines.map((l, j) =>
        wanted.has(j)
          ? { ...l, color, logoId: stillValid(l.logoId) ? l.logoId : null }
          : l
      );
    },
    /**
     * Choose a different logo for the whole product.
     *
     * Every line in the card moves together, for the same reason colour
     * does: a logo is a property of the garment, not of one size, so a
     * shopper's M and L of the same tee cannot end up carrying different
     * logos from one click.
     */
    setLogo(group, logoId) {
      const wanted = new Set(group.indices);
      this.lines = this.lines.map((l, j) =>
        wanted.has(j) ? { ...l, logoId } : l
      );
    },
    /**
     * Set the quantity of ONE size, creating or clearing its line as needed.
     *
     * Three cases, and the difference between them is what the endpoint
     * reads off the saved payload:
     *
     *   existing line, qty > 0   -> edit   (line keeps its id)
     *   existing line, qty === 0 -> remove (id + qty 0; the endpoint deletes)
     *   no line, qty > 0         -> add    (NO id; the endpoint inserts)
     *
     * A size that was added in this session and then stepped back to 0 is
     * DROPPED entirely rather than left at 0. It was never an order item, so
     * there is nothing to remove -- and an idless zero would reach the ADD
     * branch of the endpoint, which refuses zero quantities and would 400 the
     * whole save.
     *
     * Floored at 0 and capped at 99, matching what the endpoint accepts.
     */
    setSizeQty(group, row, value) {
      const next = Math.min(99, Math.max(0, Number(value) || 0));

      if (row.index != null) {
        this.patch(row.index, { qty: next });
        return;
      }

      if (next < 1) return;

      // A NEW line, built from the group's existing line so it joins the same
      // garment: colour and logo are properties of the product, not of one
      // size. `id` is deliberately absent -- its absence IS the add signal,
      // and a fabricated id would be 404'd as "not part of this order".
      const template = this.lines[group.indices[0]] || {};
      this.lines = [
        ...this.lines,
        {
          styleId: group.styleId,
          productName: template.productName,
          sku: template.sku,
          imageUrl: template.imageUrl,
          decoration: template.decoration,
          base: template.base,
          color: group.color,
          size: row.size,
          qty: next,
          logoId: group.logoId,
          availableColors: template.availableColors,
          availableSizes: template.availableSizes,
          availableLogos: template.availableLogos,
          // A new line carries no frozen discount: the backend recomputes
          // the free-item rule across the order on save. Zero, not
          // undefined, so the footer's arithmetic stays numeric.
          discount: 0,
        },
      ];
    },
    /**
     * Emit only the fields the endpoint will accept.
     *
     * The line objects carry product data for RENDERING (name, image, SKU,
     * the variant lists). None of it is editable and the endpoint refuses
     * unknown keys by name, all-or-nothing -- so sending a line verbatim
     * would 400 the whole order.
     */
    save() {
      this.$emit(
        "save",
        this.lines
          // A line with no id and no quantity was added and then cleared in
          // this same session. It does not exist on the order, so there is
          // nothing to say about it -- and saying "0" with no id would reach
          // the endpoint's ADD branch, which refuses a zero quantity and
          // 400s the whole order.
          .filter((l) => l.id != null || Number(l.qty) > 0)
          .map((l) => {
            const out = {};
            SAVABLE_KEYS.forEach((k) => {
              if (l[k] !== undefined) out[k] = l[k];
            });
            return out;
          })
      );
    },
    close() {
      this.$emit("update:modelValue", false);
    },
  },
};
</script>

<style scoped>
.bm-overlay {
  background: var(--tf-surface);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.bm-overlay__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 24px;
  border-bottom: 1px solid var(--tf-border);
  flex-shrink: 0;
}

.bm-overlay__headings {
  flex: 1;
  min-width: 0;
}

.bm-overlay__title {
  font-size: 18px;
  font-weight: 500;
  color: var(--tf-fg-1);
  margin: 0;
}

/*
  The shopper identity line, taking its treatment from the batch card's
  `.bm-order__shopper` / `.bm-order__email` rather than inventing a second
  look for the same two facts on the same surface.

  Name and email sit INLINE here, where the card stacks them, because this
  header is a single wide row rather than a narrow column -- and because
  the metadata line directly below is already a middot-separated run, so
  the two read as one identity region.

  `overflow-wrap: anywhere` is carried over from the card: a long address
  has no break opportunity and would otherwise push the close button off
  the header on a narrow window.
*/
.bm-overlay__who {
  font-size: 13px;
  color: var(--tf-fg-2);
  margin: 3px 0 0;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.bm-overlay__shopper {
  color: var(--tf-fg-1);
}

.bm-overlay__whodot {
  color: var(--tf-fg-quiet);
  margin: 0 5px;
}

.bm-overlay__email {
  color: var(--tf-primary);
  text-decoration: none;
}

.bm-overlay__email:hover {
  text-decoration: underline;
}

.bm-overlay__sub {
  font-size: 12.5px;
  color: var(--tf-fg-3);
  margin: 2px 0 0;
  line-height: 1.45;
}

/*
  The destination line. Quieter than the contact line above it: the name
  and email are things a distributor ACTS on (they will mail the person),
  whereas the address is something they CHECK. Same region, lower
  emphasis, and the truck icon lets it be found without reading.

  `align-items: flex-start` rather than `center` so the icon stays with
  the FIRST line when a long address wraps on a narrow window, instead of
  floating to the vertical middle of a two-line block.
*/
.bm-overlay__shipto {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  font-size: 12.5px;
  color: var(--tf-fg-3);
  margin: 3px 0 0;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.bm-overlay__shiptoicon {
  font-size: 15px;
  line-height: 1.35;
  color: var(--tf-fg-quiet);
  flex-shrink: 0;
}

.bm-iconbtn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  color: var(--tf-fg-4);
  min-width: 44px;
  min-height: 44px;
  align-items: center;
  justify-content: center;
}

.bm-overlay__chargenote {
  padding: 12px 24px 0;
  flex-shrink: 0;
}

.bm-overlay__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px 24px;
}

/*
  Density pass (Jake #2: "the cards are HUGE -- way too big with a ton of
  whitespace"). The chrome shrank -- padding, gaps, the per-line rule and
  the bordered radius -- while every touch target stayed at 44px, because
  mobile is a primary path. Whitespace and chrome came out; reach did not.
*/
.bm-line {
  border: 1px solid var(--tf-border);
  border-radius: var(--tf-radius-md, 12px);
  padding: 12px;
  margin-bottom: 10px;
}

.bm-line__top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.bm-line__img {
  width: 56px;
  height: 56px;
  border-radius: var(--tf-radius-xs, 4px);
  object-fit: cover;
  background: var(--tf-surface-light);
  flex-shrink: 0;
}

/*
  `--tf-fg-quiet`, not `--tf-fg-5`: this recedes but is not disabled, and
  `--tf-fg-5` is the 2.68:1 disabled grey (accessibility ratchet in
  styles/__tests__/quiet-fg-contrast.spec.js).
*/
.bm-line__img--none {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tf-fg-quiet);
  border: 1px dashed var(--tf-border-strong);
}

.bm-line__img--none .material-icons {
  font-size: 22px;
}

.bm-line__ident {
  flex: 1;
  min-width: 0;
}

.bm-line__name {
  font-size: 14.5px;
  font-weight: 500;
  color: var(--tf-fg-1);
  line-height: 1.3;
}

.bm-line__meta {
  font-size: 12px;
  color: var(--tf-fg-4);
  margin-top: 2px;
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.bm-line__amount {
  font-size: 14.5px;
  font-weight: 500;
  color: var(--tf-fg-1);
  white-space: nowrap;
}

.bm-line__controls {
  display: flex;
  gap: 20px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.bm-line__colors {
  min-width: 150px;
}

.bm-line__sizes {
  flex: 1;
  min-width: 220px;
}

.bm-line__logos {
  min-width: 190px;
}

.bm-logos {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/*
  A row rather than a chip: the design code is the point of this control on
  an internal surface, and it does not fit a swatch. 44px minimum for the
  same reason the steppers have one -- mobile is a primary path.
*/
.bm-logo {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 44px;
  padding: 4px 9px 4px 4px;
  border: 1px solid var(--tf-border-strong);
  border-radius: var(--tf-radius-md, 12px);
  background: var(--tf-surface);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.bm-logo--on {
  border: 2px solid var(--tf-primary);
  box-shadow: 0 0 0 3px var(--tf-primary-soft);
}

.bm-logo__img {
  width: 34px;
  height: 34px;
  border-radius: var(--tf-radius-xs, 4px);
  object-fit: contain;
  background: var(--tf-surface-light);
  flex-shrink: 0;
}

/*
  `--tf-fg-quiet`, not `--tf-fg-5`: a missing thumbnail recedes but is not
  disabled, and `--tf-fg-5` is the 2.68:1 disabled grey pinned by
  styles/__tests__/quiet-fg-contrast.spec.js.
*/
.bm-logo__img--none {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tf-fg-quiet);
  border: 1px dashed var(--tf-border-strong);
}

.bm-logo__img--none .material-icons {
  font-size: 18px;
}

.bm-logo__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.bm-logo__name {
  font-size: 13px;
  color: var(--tf-fg-2);
  line-height: 1.3;
}

.bm-logo__code {
  font-size: 11px;
  color: var(--tf-fg-4);
  font-variant-numeric: tabular-nums;
}

.bm-field__label {
  font-size: 11.5px;
  color: var(--tf-primary);
  display: block;
  margin-bottom: 5px;
}

.bm-field__value {
  color: var(--tf-fg-3);
}

/*
  SIX SWATCHES TO A ROW, BY TRACK COUNT. Jake, 2026-09-22: "Let's limit the
  number of colors on a row to 6." The L500 Women's Silk Touch Polo carries
  22 colours and rendered all of them in one unbroken row, which dominated
  the card and pushed the size grid and everything under it down the page.

  THE OLD RULE ALREADY WRAPPED, AND THAT IS THE WHOLE POINT. It said
  `display: flex; gap: 8px; flex-wrap: wrap`, so "it does not wrap" was
  never the bug and adding another wrap would have changed nothing. A 32px
  swatch on an 8px gap is a 40px pitch, and 22 of those fit inside this
  card's ~880px colour column -- wrapping was enabled and simply never
  reached. Only a constraint that does not depend on the container width
  fixes it, and a six-track grid is exactly that: a grid cannot place a
  seventh item on a row that has six columns, at any card width.

  A max-width would have been the other option and is weaker: it has to be
  re-derived whenever the swatch size or gap changes, and it has to hold at
  every width this panel takes. The track count holds by construction.

  FIXED 32px TRACKS, NOT `1fr` -- the mistake #3705 made on the size band
  next door. `1fr` in a ~1036px card spreads six 32px swatches into 166px
  cells of mostly dead space, which is the same whitespace complaint this
  change exists to remove. `justify-content: start` keeps the short case
  left-aligned rather than letting a three-colour product drift.

  `max-content` on the container stops the grid claiming the full column
  width when a product has fewer than six colours.

  THE 8px GAP IS LOAD-BEARING FOR A TEST, not just for looks. The
  dm-order-editor-color-swatch-rows scenario asserts swatch 7 is flush-LEFT
  with swatch 1, and `_flush_distance` takes min(same-edge, facing-edge) --
  so on the broken one-row layout the distance it reports is the run of
  gaps between them. A gap at or below the scenario's 0.5px tolerance would
  make that assertion vacuous. Keep this well above it.
*/
.bm-swatches {
  display: grid;
  grid-template-columns: repeat(6, 32px);
  gap: 8px;
  justify-content: start;
  width: max-content;
}

.bm-swatch {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  border: 1px solid var(--tf-border-strong);
  /*
    The swatch is a 32px dot inside a 44px hit area -- the visual shrank
    for density, the touch target did not.
  */
  outline-offset: 6px;
  position: relative;
}

.bm-swatch::after {
  content: "";
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
}

.bm-swatch--on {
  border: 2px solid var(--tf-primary);
  box-shadow: 0 0 0 3px var(--tf-primary-soft);
}

/* No hex from the supplier: name it rather than show a lying colour. */
.bm-swatch--nohex {
  background: var(--tf-surface-light);
  font-size: 9px;
  color: var(--tf-fg-4);
  display: flex;
  align-items: center;
  justify-content: center;
}

/*
  A HORIZONTAL band, taking its shape from the shopper PDP's desktop size
  matrix (`sf-product__matrix-grid` in ProductPage.vue).

  FIXED 72px TRACKS, NOT THE PDP'S `minmax(56px, 1fr)`. This is the one
  place the two surfaces must differ, and copying the PDP literally was
  wrong -- I shipped it that way first and measured the result. The PDP's
  `1fr` is harmless there because its buy box is ~340px wide, so a track
  never stretches far. This card is ~1036px wide, and `1fr` spread six
  sizes into 166px cells holding a 44px stepper: 122px of dead space per
  cell, measured in the browser. That is the SAME whitespace complaint
  that prompted this fix, rotated onto the horizontal axis.

  72px = the 44px stepper + padding, with room for a three-character size
  label. Tracks that do not stretch keep the band compact and
  left-aligned, which is what the PDP actually looks like even though its
  declaration says otherwise.

  `auto-fit` still wraps to a second line when the sizes outrun the card,
  and keeps the columns equal when it does, so a long run reads as a
  matrix rather than as a ragged line. `flex-wrap` would leave the last
  line's cells a different width from the first's.

  A SINGLE size needs no special case under fixed tracks -- it simply
  occupies one 72px column. The PDP needs its `is-single-size` rule only
  because its `1fr` would stretch a lone cell across the whole buy box.

  THE 8px GAP IS LOAD-BEARING FOR A TEST, not just for looks. The
  dm-order-editor-all-sizes-and-filter scenario asserts two adjacent cells
  share a top edge, and `_flush_distance` compares against the facing edge
  as well as the same edge -- so in a stacked column the distance it
  reports is this gap. A gap at or below the scenario's 0.5px tolerance
  would make that assertion vacuous. If you change this value, keep it
  well above the tolerance.

  PREVIOUS STATE, and why it is worth recording: this rule said
  `display: flex; flex-direction: column`, while the class was named
  `bm-sizegrid` and a comment above the markup claimed it followed the PDP
  matrix. The name and the comment were both right about the intent and
  both wrong about the behaviour, and the CSS for the cells the template
  actually renders (`.bm-sizecell`) did not exist at all -- so each cell
  was an unstyled full-width block. Jake rejected it on sight.
*/
.bm-sizegrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, 72px);
  gap: 8px;
}

.bm-sizecell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 7px 2px;
  border: 1px solid var(--tf-border);
  border-radius: 8px;
  box-sizing: border-box;
}

.bm-sizecell__label {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--tf-fg-2);
  text-align: center;
}

/*
  An unordered size is de-emphasized, NOT disabled -- "not ordered" is
  information a distributor has to be able to read, and every size is
  steppable now. `--tf-fg-quiet` is the muted-but-AA-compliant token;
  `--tf-fg-5` would be 2.68:1.
*/
.bm-sizecell--empty .bm-sizecell__label {
  font-weight: 500;
  color: var(--tf-fg-quiet);
}

.bm-line__scopenote {
  font-size: 11.5px;
  color: var(--tf-fg-4);
  margin: 8px 0 0;
  line-height: 1.5;
}

/*
 * The impasse note sits where a picker would be, so it takes the top
 * margin off -- it IS the field's content, not a footnote under one.
 *
 * Deliberately NOT given an error colour or an icon. The order is still
 * saveable; styling this like a failure would contradict the behaviour
 * it is describing and send a distributor looking for something to fix.
 */
.bm-line__scopenote--impasse {
  margin-top: 0;
}

.bm-pricelock {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  background: var(--tf-surface-light);
  border: 1px solid var(--tf-border);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 12px;
  color: var(--tf-fg-3);
  line-height: 1.5;
}

.bm-pricelock b {
  font-weight: 500;
  color: var(--tf-fg-2);
}

.bm-pricelock__icon {
  font-size: 16px;
  margin-top: 1px;
  color: var(--tf-fg-4);
  flex-shrink: 0;
}

.bm-overlay__foot {
  flex-shrink: 0;
  border-top: 1px solid var(--tf-border);
  background: var(--tf-surface);
  padding: 12px 24px;
}

.bm-editfoot {
  display: flex;
  gap: 14px;
  align-items: center;
}

.bm-editfoot__totals {
  flex: 1;
  min-width: 0;
}

.bm-editfoot__lines {
  margin-bottom: 4px;
}

.bm-editfoot__line {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12.5px;
  color: var(--tf-fg-3);
  max-width: 260px;
}

.bm-editfoot__amounts {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.bm-editfoot__was {
  font-size: 14px;
  color: var(--tf-fg-quiet);
  text-decoration: line-through;
}

.bm-editfoot__now {
  font-size: 22px;
  font-weight: 500;
  color: var(--tf-fg-1);
}

.bm-editfoot__delta {
  font-size: 13px;
  font-weight: 500;
}

.bm-editfoot__delta--up {
  color: var(--tf-chip-pending-fg);
}

.bm-editfoot__delta--down {
  color: var(--tf-success-dark);
}

.bm-editfoot__sentence {
  font-size: 12px;
  color: var(--tf-fg-3);
  margin-top: 2px;
}

.bm-editfoot__actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.bm-btn {
  border-radius: var(--tf-radius-pill, 9999px);
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 44px;
  padding: 9px 18px;
  font-size: 13.5px;
  border: 1px solid var(--tf-border-strong);
  background: var(--tf-surface);
  color: var(--tf-fg-2);
}

.bm-btn--primary {
  background: var(--tf-primary);
  border-color: var(--tf-primary);
  color: var(--tf-fg-on-primary);
}

.bm-btn--danger {
  background: var(--tf-error);
  border-color: var(--tf-error);
  color: var(--tf-fg-on-primary);
}

/*
  Cancel sits beside Discard and Save now, so it is outlined rather than
  solid red: a filled destructive button next to the primary action reads
  as the thing to press. The confirm dialog still carries the red.
*/
.bm-btn--dangerquiet {
  background: var(--tf-surface);
  border-color: var(--tf-chip-error-border);
  color: var(--tf-error);
}

.bm-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.bm-scrim {
  position: absolute;
  inset: 0;
  background: rgba(19, 55, 82, 0.55);
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.bm-confirm {
  background: var(--tf-surface);
  border-radius: 12px;
  width: min(460px, 100%);
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
}

.bm-confirm__head {
  background: var(--tf-error);
  color: var(--tf-fg-on-primary);
  padding: 14px 20px;
  font-size: 17px;
  font-weight: 500;
}

.bm-confirm__body {
  padding: 18px 20px;
  font-size: 13.5px;
  color: var(--tf-fg-2);
  line-height: 1.6;
}

.bm-confirm__foot {
  padding: 12px 20px 18px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* Mobile is a primary path. */
@media (max-width: 599px) {
  .bm-overlay__head,
  .bm-overlay__body,
  .bm-overlay__foot {
    padding-left: 16px;
    padding-right: 16px;
  }

  .bm-overlay__chargenote {
    padding: 12px 16px 0;
  }

  .bm-line__controls {
    flex-direction: column;
    gap: 14px;
  }

  .bm-editfoot {
    flex-direction: column;
    align-items: stretch;
  }

  .bm-editfoot__actions {
    flex-direction: column-reverse;
  }

  .bm-editfoot__actions .bm-btn {
    width: 100%;
  }

  .bm-confirm__foot {
    flex-direction: column-reverse;
  }

  .bm-confirm__foot .bm-btn {
    width: 100%;
  }
}
</style>
